/**
 * 任务数据适配器
 *
 * 职责：
 * 1. API DTO → Domain Model 转换
 * 2. Domain Model → ViewModel 转换
 * 3. 处理后端字段命名差异（snake_case → camelCase）
 *
 * @version 4.0.0
 * @author Frontend Team
 * @since 2026-01-23
 */

import type { TaskStatus } from '@/types/task.dto'
import type { Task, TaskItem, TTSSegment, BatchFile } from '@/types/task.domain'
import type { TaskViewModel, TaskItemViewModel } from '@/types/task.viewmodel'
import { createTaskViewModel, createTaskItemViewModel } from '@/types/task.viewmodel'

// ============= DTO → Domain 适配器 =============

/**
 * 适配 Task DTO → Domain
 */
export function adaptTaskDTO(dto: any): Task {
  return {
    // 基础标识
    taskId: dto.taskId || dto.task_id || '',
    type: dto.type || 'single',
    title: dto.title || '',
    description: dto.description,

    // 状态与进度
    status: normalizeTaskStatus(dto.status),
    priority: dto.priority || 'normal',
    progress: dto.progress || 0,

    // 用户信息
    userId: dto.userId || dto.user_id || 0,
    guestId: dto.guestId || dto.guest_id,
    userTier: dto.userTier || dto.user_tier,

    // 关联字段
    errorMessage: dto.errorMessage || dto.error_message,
    parentTaskId: dto.parentTaskId || dto.parent_task_id,
    primaryAssetId: dto.primaryAssetId || dto.primary_asset_id,

    // 配置与元数据
    config: dto.config,
    metadata: dto.metadata,

    // 时间信息
    createdAt: dto.createdAt || dto.created_at || new Date().toISOString(),
    updatedAt: dto.updatedAt || dto.updated_at,
    startedAt: dto.startedAt || dto.started_at,
    completedAt: dto.completedAt || dto.completed_at,

    // 统计字段
    totalItems: dto.totalItems || dto.total_items,
    completedItems: dto.completedItems || dto.completed_items,
    failedItems: dto.failedItems || dto.failed_items,

    // 任务项列表
    items: dto.items?.map(adaptTaskItemDTO) || []
  }
}

/**
 * 适配 TaskItem DTO → Domain
 */
export function adaptTaskItemDTO(dto: any): TaskItem {
  return {
    // 基础标识
    id: dto.id || '',
    taskId: dto.taskId || dto.task_id || '',

    // 业务类型标识
    bizType: dto.bizType || dto.biz_type || 'tts_segment',
    bizItemRef: dto.bizItemRef || dto.biz_item_ref || '',

    // 执行信息
    sequenceIndex: dto.sequenceIndex || dto.sequence_index || 0,
    status: normalizeTaskItemStatus(dto.status),
    progress: dto.progress || 0,
    errorMessage: dto.errorMessage || dto.error_message,

    // 配置与指标
    configOverride: dto.configOverride || dto.config_override,
    metrics: dto.metrics,

    // 时间信息
    startedAt: dto.startedAt || dto.started_at,
    completedAt: dto.completedAt || dto.completed_at,

    // 业务数据
    segment: dto.segment ? adaptTTSSegmentDTO(dto.segment) : undefined,
    file: dto.file ? adaptBatchFileDTO(dto.file) : undefined,
    // @ts-ignore 短剧配音专用字段，通过 any 透传
    dubbingLine: dto.dubbingLine || undefined
  }
}

/**
 * 适配 TTSSegment DTO → Domain
 */
export function adaptTTSSegmentDTO(dto: any): TTSSegment {
  return {
    // 业务标识
    uid: dto.uid || '',
    taskId: dto.taskId || dto.task_id || '',

    // TTS 参数
    text: dto.text || '',
    ssml: dto.ssml,
    voice: dto.voice || '',
    speed: dto.speed,
    pitch: dto.pitch,
    volume: dto.volume,
    format: dto.format,
    style: dto.style,
    role: dto.role,

    // 成果物
    primaryAssetId: dto.primaryAssetId || dto.primary_asset_id,
    audioUrl: dto.audioUrl || dto.audio_url,
    durationSec: dto.durationSec || dto.duration_sec,
    fileSize: dto.fileSize || dto.file_size,
    filename: dto.filename,

    // 字幕相关
    subtitleEnabled: dto.subtitleEnabled || dto.subtitle_enabled,
    subtitleFormat: dto.subtitleFormat || dto.subtitle_format,
    subtitleAssetId: dto.subtitleAssetId || dto.subtitle_asset_id,
    subtitleUrl: dto.subtitleUrl || dto.subtitle_url,
    boundaryJsonUrl: dto.boundaryJsonUrl || dto.boundary_json_url,
    subtitleSrtUrl: dto.subtitleSrtUrl || dto.subtitle_srt_url,
    subtitleVttUrl: dto.subtitleVttUrl || dto.subtitle_vtt_url,
    subtitleAssUrl: dto.subtitleAssUrl || dto.subtitle_ass_url,

    // 资产过期状态（后端计算，前端据此决定如何显示）
    assetStatus: dto.assetStatus || dto.asset_status,
    expiresAt: dto.expiresAt ?? dto.expires_at,
    timeRemaining: dto.timeRemaining ?? dto.time_remaining,
    canPromote: dto.canPromote ?? dto.can_promote ?? false
  }
}

/**
 * 适配 BatchFile DTO → Domain
 */
export function adaptBatchFileDTO(dto: any): BatchFile {
  return {
    // 业务标识
    uid: dto.uid || '',
    taskId: dto.taskId || dto.task_id,
    userId: dto.userId || dto.user_id || 0,

    // 文件信息
    originalName: dto.originalName || dto.original_name || '',
    contentHash: dto.contentHash || dto.content_hash || '',
    totalChars: dto.totalChars || dto.total_chars || 0,
    parsedContent: dto.parsedContent || dto.parsed_content || '',

    // 状态
    status: dto.status || 'uploaded',
    errorMessage: dto.errorMessage || dto.error_message,

    // 音频信息
    voice: dto.voice,
    format: dto.format,
    primaryAssetId: dto.primaryAssetId || dto.primary_asset_id,
    audioUrl: dto.audioUrl || dto.audio_url,
    durationSec: dto.durationSec || dto.duration_sec,
    fileSize: dto.fileSize || dto.file_size,
    filename: dto.filename,

    // 时间信息
    createdAt: dto.createdAt || dto.created_at || new Date().toISOString(),
    updatedAt: dto.updatedAt || dto.updated_at,

    // 资产过期状态（后端计算，前端据此决定如何显示）
    assetStatus: dto.assetStatus || dto.asset_status,
    expiresAt: dto.expiresAt ?? dto.expires_at,
    timeRemaining: dto.timeRemaining ?? dto.time_remaining,
    canPromote: dto.canPromote ?? dto.can_promote ?? false
  }
}

// ============= 批量适配 =============

/**
 * 批量适配任务
 */
export function adaptTasks(dtos: any[]): Task[] {
  if (!Array.isArray(dtos)) return []
  return dtos.map(adaptTaskDTO)
}

/**
 * 批量适配任务项
 */
export function adaptTaskItems(dtos: any[]): TaskItem[] {
  if (!Array.isArray(dtos)) return []
  return dtos.map(adaptTaskItemDTO)
}

// ============= Domain → ViewModel 转换 =============

/**
 * 转换为 Task ViewModel
 */
export function toTaskViewModel(task: Task): TaskViewModel {
  return createTaskViewModel(task)
}

/**
 * 批量转换为 Task ViewModel
 */
export function toTaskViewModels(tasks: Task[]): TaskViewModel[] {
  return tasks.map(createTaskViewModel)
}

/**
 * 转换为 TaskItem ViewModel
 */
export function toTaskItemViewModel(item: TaskItem): TaskItemViewModel {
  return createTaskItemViewModel(item)
}

// ============= 辅助函数 =============

/**
 * 标准化任务状态
 */
function normalizeTaskStatus(status: any): TaskStatus {
  if (!status) return 'created'

  const statusStr = String(status).toLowerCase()
  const statusMap: Record<string, TaskStatus> = {
    'pending': 'created',
    'waiting': 'created',
    'created': 'created',
    'queued': 'queued',
    'processing': 'processing',
    'running': 'processing',
    'post_processing': 'post_processing',
    'completed': 'completed',
    'success': 'completed',
    'finished': 'completed',
    'failed': 'processing_failed',
    'error': 'processing_failed',
    'processing_failed': 'processing_failed',
    'post_failed': 'post_failed',
    'cancelled': 'cancelled',
    'canceled': 'cancelled'
  }

  return statusMap[statusStr] || 'created'
}

/**
 * 标准化任务项状态
 */
function normalizeTaskItemStatus(status: any): 'pending' | 'running' | 'completed' | 'failed' {
  if (!status) return 'pending'

  const statusStr = String(status).toLowerCase()

  if (['pending', 'waiting', 'created', 'queued'].includes(statusStr)) {
    return 'pending'
  }

  if (['processing', 'running', 'executing'].includes(statusStr)) {
    return 'running'
  }

  if (['completed', 'success', 'succeeded', 'finished'].includes(statusStr)) {
    return 'completed'
  }

  if (['failed', 'error', 'cancelled'].includes(statusStr)) {
    return 'failed'
  }

  return 'pending'
}

// ============= 导出工具集合 =============

export const taskAdapter = {
  // DTO → Domain
  adaptTaskDTO,
  adaptTaskItemDTO,
  adaptTTSSegmentDTO,
  adaptBatchFileDTO,
  adaptTasks,
  adaptTaskItems,

  // Domain → ViewModel
  toTaskViewModel,
  toTaskViewModels,
  toTaskItemViewModel
}

export default taskAdapter
